import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@matix/auth';
import type { CallCenterOrder } from '@matix/types';
import { useLang } from '../../lib/language';
import { ToastHost } from '../../components/Toast';
import { callCenterApi } from './api';
import { useLiveOrders, useServerClock } from './hooks';
import { Monitor } from './Monitor';
import { NewOrderScreen } from './NewOrderScreen';
import { OrderCard } from './OrderCard';
import { OrderDetailDrawer } from './OrderDetailDrawer';
import { useCallCenter } from './store';

/**
 * The agent's surface: take an order, watch the board, read the numbers.
 *
 * A full screen rather than a tab inside the till shell, because an agent with
 * the Call Center permission is not selling at a counter — there is no drawer,
 * no shift, no cart, and the till's sidebar and topbar are all controls for a
 * job they are not doing. Mounting the POS shell around this would put a
 * "Charge" button next to somebody whose customer is on the phone.
 *
 * Three tabs and no more. Anything a supervisor needs beyond these lives in the
 * admin app, which is where configuration and history belong.
 */
type Tab = 'new' | 'board' | 'monitor';

export function CallCenterScreen(): JSX.Element {
  const { t, lang, toggle } = useLang();
  const { cashier, logout } = useAuth();
  const [tab, setTab] = useState<Tab>('new');
  const bootstrap = useCallCenter((s) => s.bootstrap);

  // Hoisted out of the JSX: a hook inside `tab === 'monitor' && …` is a
  // conditional hook call, which React forbids and which would blow up the
  // moment an agent switched tabs.
  const loadDashboard = useCallback(
    (range: { from?: string; to?: string }) => callCenterApi.agent.dashboard(range),
    [],
  );

  return (
    <div className="cc-app">
      <header className="cc-topbar">
        <div className="cc-brand">
          <span className="cc-brand-mark">M</span>
          <span className="cc-brand-text">{t({ en: 'Call Center', ar: 'مركز الاتصال' })}</span>
        </div>

        <nav className="cc-tabs cc-tabs--top">
          <button
            type="button"
            className={`cc-tab ${tab === 'new' ? 'cc-tab--on' : ''}`}
            onClick={() => setTab('new')}
          >
            {t({ en: 'New order', ar: 'طلب جديد' })}
          </button>
          <button
            type="button"
            className={`cc-tab ${tab === 'board' ? 'cc-tab--on' : ''}`}
            onClick={() => setTab('board')}
          >
            {t({ en: 'Live board', ar: 'اللوحة المباشرة' })}
          </button>
          <button
            type="button"
            className={`cc-tab ${tab === 'monitor' ? 'cc-tab--on' : ''}`}
            onClick={() => setTab('monitor')}
          >
            {t({ en: 'Dashboard', ar: 'لوحة المتابعة' })}
          </button>
        </nav>

        <div className="cc-topbar-right">
          <button type="button" className="cc-link" onClick={toggle}>
            {lang === 'ar' ? 'EN' : 'ع'}
          </button>
          <span className="cc-user">{cashier?.username ?? ''}</span>
          <button type="button" className="cc-link cc-link--danger" onClick={() => void logout()}>
            {t({ en: 'Sign out', ar: 'تسجيل الخروج' })}
          </button>
        </div>
      </header>

      <main className="cc-main">
        {tab === 'new' && <NewOrderScreen />}
        {tab === 'board' && <LiveBoard />}
        {tab === 'monitor' && <Monitor load={loadDashboard} showBranches />}
      </main>

      {!bootstrap?.provisioned && tab !== 'new' && (
        <div className="cc-empty">
          {t({
            en: 'The Call Center module is not installed on this tenant.',
            ar: 'وحدة مركز الاتصال غير مثبتة لهذا المستأجر.',
          })}
        </div>
      )}

      {/*
        The agent surface REPLACES LegacyApp, which is where the till's
        ToastHost lives — so without this every toast raised here rendered
        nowhere. That is not a cosmetic loss: a failed Send Order reports
        through `toast(...)`, so the agent saw the button do nothing and no
        error at all. Mounted at the shell so every tab is covered.
      */}
      <ToastHost />
    </div>
  );
}

/**
 * Every open order, every branch, counting up.
 *
 * The agent's answer to "where is my order?" — which is the second most common
 * sentence on a call-centre phone line, and the one a system either answers in
 * three seconds or does not answer at all.
 */
function LiveBoard(): JSX.Element {
  const { t } = useLang();
  const bootstrap = useCallCenter((s) => s.bootstrap);
  const [branchFilter, setBranchFilter] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [overrides, setOverrides] = useState<Record<number, CallCenterOrder>>({});

  const params = useMemo(
    () => ({
      open_only: true,
      ...(branchFilter != null ? { branch_id: branchFilter } : {}),
      ...(query.trim() ? { q: query.trim() } : {}),
    }),
    [branchFilter, query],
  );

  const live = useLiveOrders({
    mode: 'all',
    params,
    pollSeconds: bootstrap?.settings.pollSeconds ?? 15,
  });
  const now = useServerClock(live.serverTime);

  const orders = live.orders.map((o) => overrides[o.id] ?? o);

  return (
    <div className="cc-board">
      <header className="cc-board-head">
        <input
          className="cc-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t({ en: 'Phone, name or order number', ar: 'الهاتف أو الاسم أو رقم الطلب' })}
        />
        <select
          className="cc-select"
          value={branchFilter ?? ''}
          onChange={(e) => setBranchFilter(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{t({ en: 'All branches', ar: 'كل الفروع' })}</option>
          {(bootstrap?.branches ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <span className={`cc-live ${live.live ? 'cc-live--on' : ''}`}>
          {live.live ? t({ en: 'Live', ar: 'مباشر' }) : t({ en: 'Polling', ar: 'تحديث دوري' })}
        </span>
        <button type="button" className="cc-link" onClick={live.refresh}>
          {t({ en: 'Refresh', ar: 'تحديث' })}
        </button>
      </header>

      {live.error && <div className="cc-warn">{live.error}</div>}

      {!live.loading && orders.length === 0 && (
        <div className="cc-empty">
          <div className="cc-empty-title">{t({ en: 'All clear', ar: 'لا يوجد شيء' })}</div>
          <p>{t({ en: 'No open orders right now.', ar: 'لا توجد طلبات مفتوحة حالياً.' })}</p>
        </div>
      )}

      <div className="cc-grid">
        {orders.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            nowMs={now()}
            warnSeconds={live.warnSeconds}
            criticalSeconds={live.criticalSeconds}
            onOpen={() => setDetailId(order.id)}
            showBranch
          >
            <button type="button" className="cc-btn" onClick={() => setDetailId(order.id)}>
              {t({ en: 'Details', ar: 'التفاصيل' })}
            </button>
          </OrderCard>
        ))}
      </div>

      <OrderDetailDrawer
        orderId={detailId}
        mode="agent"
        onClose={() => setDetailId(null)}
        onChanged={(o) => {
          setOverrides((prev) => ({ ...prev, [o.id]: o }));
          live.refresh();
        }}
      />
    </div>
  );
}
